import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import Icon from "@mdi/react";
import {
  mdiAlertCircleOutline,
  mdiDeleteOutline,
  mdiOpenInNew,
  mdiPencilOutline,
  mdiPlus,
} from "@mdi/js";
import InfoTooltip from "app/components/common/InfoTooltip";
import EmailAutomationRow from "./EmailAutomationRow";
import EmailActivityPanel from "./EmailActivityPanel";
import {
  hasSummaryData,
  getEmailCounts,
} from "app/containers/JobCalendar/utils/emailAutomationHelpers";
import moment from "moment-timezone";
import { TOOLTIP_DELAY } from "./InfoIconSlot";
import JobPopoverPeople from "./JobPopoverPeople";
import { JobResultsInfo, JobSetupInfo } from "./JobPopoverJobInfo";
import "./JobPopover.css";

const JobPopover = ({
  show,
  onHide,
  appointment,
  onDelete,
  onJobBooking,
  onJobSetup,
  onCopyContent,
  onContactForHelp,
  loading = false,
  // Read-only glance mode. Management actions live on Job Detail; the
  // anomaly banner becomes the only path there. Opt-in so the Calendar keeps its
  // current behavior until jobs + appointments are unified on the backend.
  readOnly = false,
  needsAttention = false,
  onViewJobDetail,
}) => {
  const [showEmailPanel, setShowEmailPanel] = useState(false);
  const [localEmailSummary, setLocalEmailSummary] = useState(null);

  useEffect(() => {
    if (!show) {
      setShowEmailPanel(false);
      setLocalEmailSummary(null);
    }
  }, [show]);

  // Early return if no appointment
  if (!appointment) return null;

  // Extract status from appointment
  const status = appointment?.tag || appointment?.status || "booked";

  // Status colors live in jobPopover.css as --<status> modifier classes.
  const getStatusInfo = (status) => {
    const normalizedStatus = status?.toLowerCase();
    switch (normalizedStatus) {
      case "booked":
        return { modifier: "booked", text: "Booked" };
      case "pre-inspection":
        return { modifier: "pre-inspection", text: "Pre-Inspection" };
      case "under-inspection":
        return { modifier: "under-inspection", text: "Under Inspection" };
      case "completed":
        return { modifier: "completed", text: "Completed" };
      default:
        return { modifier: "booked", text: "Booked" };
    }
  };

  const statusInfo = getStatusInfo(status);

  // Check if it's mobile and truncate status text if needed
  const isMobile = window.innerWidth <= 480;
  const displayStatusText =
    isMobile && statusInfo.text.length > 6
      ? statusInfo.text.substring(0, 3) + "..."
      : statusInfo.text;

  // Format time display
  const formatTimeDisplay = () => {
    try {
      const startTime = moment(
        (appointment.start_time || appointment.start).substring(0, 19)
      );
      const endTime = moment(
        (appointment.end_time || appointment.end).substring(0, 19)
      );
      return `${startTime.format("MMM D, h:mm A")} - ${endTime.format("h:mm A")}`;
    } catch (error) {
      console.error("Error formatting time:", error);
      return "Time unavailable";
    }
  };

  // Compute display data
  const timeDisplay = formatTimeDisplay();
  const normalizedStatus = status.toLowerCase();
  const emailSummary = localEmailSummary || getEmailCounts(appointment.job);

  return (
    <Modal show={show} onHide={onHide} centered className="job-popover-modal">
      <Modal.Header closeButton={false} className="job-popover-header">
        <div className="job-popover-time">
          <span>{timeDisplay}</span>
          {appointment.note && (
            <InfoTooltip
              text={appointment.note}
              delay={TOOLTIP_DELAY}
              className="custom-tooltip"
              iconSize="20px"
              iconClassName="job-popover-time-info"
            />
          )}
        </div>
        <div
          className={`job-popover-status-badge job-popover-status-badge--${statusInfo.modifier}`}
        >
          {displayStatusText}
        </div>
      </Modal.Header>

      <Modal.Body className="job-popover-body">
        {showEmailPanel ? (
          <EmailActivityPanel
            appointment={appointment}
            onBack={() => setShowEmailPanel(false)}
            onCopyContent={onCopyContent}
            onContactForHelp={onContactForHelp}
            onSummaryUpdate={setLocalEmailSummary}
          />
        ) : loading ? (
          <div className="job-popover-loading">
            <div className="d-flex justify-content-center align-items-center job-popover-loading-body">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">
                  Loading appointment details...
                </span>
              </div>
              <span className="ms-3">Loading appointment details...</span>
            </div>
          </div>
        ) : (
          <div className="job-popover-content">
            <JobPopoverPeople appointment={appointment} />

            {normalizedStatus !== "booked" && (
              <JobSetupInfo appointment={appointment} />
            )}

            {/* Anomaly banner — the only path to Job Detail in read-only mode */}
            {readOnly && needsAttention && onViewJobDetail && (
              <div className="job-anomaly-banner">
                <Icon
                  path={mdiAlertCircleOutline}
                  size="16px"
                  className="job-anomaly-banner-icon"
                />
                <span className="job-anomaly-banner-text">
                  This job needs attention
                </span>
                <Button
                  variant="link"
                  className="job-anomaly-banner-link"
                  onClick={onViewJobDetail}
                >
                  <Icon path={mdiOpenInNew} size="16px" />
                  View Job Detail
                </Button>
              </div>
            )}

            <JobResultsInfo
              appointment={appointment}
              showReport={normalizedStatus === "completed"}
            />

            {/* Email Automation */}
            {hasSummaryData(emailSummary) && (
              <>
                <div className="job-popover-divider" />
                <div className="job-info">
                  <EmailAutomationRow
                    summary={emailSummary}
                    onClick={() => setShowEmailPanel(true)}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </Modal.Body>

      {/* Footer with management actions — hidden in read-only glance mode and
          while the email activity panel is open */}
      {!readOnly && !showEmailPanel && (
        <Modal.Footer className="job-popover-footer">
          <Button
            variant="outline-danger"
            size="sm"
            className="job-popover-delete-btn"
            onClick={() => onDelete && onDelete(appointment)}
            disabled={loading}
            aria-label="Delete job"
          >
            <Icon path={mdiDeleteOutline} size="20px" />
          </Button>

          <div className="job-popover-footer-actions">
            <Button
              variant="outline-secondary"
              size="sm"
              className="job-popover-booking-btn"
              onClick={() => onJobBooking && onJobBooking(appointment)}
              disabled={loading}
            >
              <Icon path={mdiPencilOutline} size="16px" />
              Job Booking
            </Button>

            <Button
              variant={
                normalizedStatus === "booked" ? "primary" : "outline-secondary"
              }
              size="sm"
              className="job-popover-setup-btn"
              onClick={() => onJobSetup && onJobSetup(appointment)}
              disabled={loading}
            >
              <Icon
                path={
                  normalizedStatus === "booked" ? mdiPlus : mdiPencilOutline
                }
                size="16px"
              />
              Job Setup
            </Button>
          </div>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default JobPopover;
